import Immutable from 'immutable';
import {fromJSOrdered} from '~/utils/immutable';
import {
  currentColorUrlOverride,
  currentStyleUrlOverride,
  quantitiesUrlOverride,
  selectionsUrlOverride
} from '~/utils/url';
import Product from '~/data/records/Product';
import {initialState as initialCanvasState} from '~/reducers/Canvas';
import {initialState as initialDesignState} from '~/reducers/core/Design';
import {
  deserializeIcon,
  deserializeNamesAndNumbers as deserializeNamesAndNumbersBase
} from '~/data/deserializers/base';

// TODO: Undoable branches must be wrappd in getUndoableDeserializer

export const getUndoableDeserializer = deserializer => data => {
  return {
    future: {},
    past: {},
    present: deserializer(data)
  };
};

export const deserializeCanvas = getUndoableDeserializer(data => {
  const {canvas, icon} = data;
  const iconIds = Object.keys(icon);

  const now = Date.now();
  // Filter out any rogue upload iconIds without a corresponding icon
  const recentUploadIconIds = canvas.recentUploadIconIds
    .filter(id => iconIds.includes(id))
    .filter(id => {
      const expirationDate = icon[id]?.expirationDate;
      return expirationDate && expirationDate >= now;
    });
  const localCanvasState = Immutable.Map({
    recentUploadIconIds: Immutable.OrderedSet(recentUploadIconIds)
  });
  return initialCanvasState().merge(localCanvasState);
});

export const deserializeDesigns = getUndoableDeserializer(data => {
  const {design: designs} = data;
  const localDesignState = Immutable.Map(designs).map(design =>
    Immutable.Map({
      ...design,
      fetching: false,
      iconIds: Immutable.List(design.iconIds)
    })
  );
  return localDesignState.merge(initialDesignState());
});

export const deserializeFundraising = data => {
  const {
    goalQuantity,
    salePrices,
    printMinimums,
    profits,
    minProfit,
    maxProfit
  } = data.fundraising;
  return Immutable.Map({
    goalQuantity,
    salePrices: Immutable.fromJS(salePrices),
    printMinimums: Immutable.fromJS(printMinimums),
    profits: Immutable.fromJS(profits),
    minProfit,
    maxProfit,
    skipOverview: false,
    fetching: false
  });
};

export const deserializeIcons = getUndoableDeserializer(data => {
  const {icon: icons} = data;
  return new Immutable.Map(icons)
    .map(icon => deserializeIcon(icon))
    .filter(icon => icon !== undefined);
});

export const deserializeNamesAndNumbers = getUndoableDeserializer(data =>
  deserializeNamesAndNumbersBase(data.namesAndNumbers)
);

export const deserializeProduct = data => {
  const {product} = data;
  const {selections, currentColorId, anchorStyleId} = product;
  return Immutable.Map({
    anchorStyleId: currentStyleUrlOverride(anchorStyleId),
    categoryPaths: Immutable.Map(product.categoryPaths),
    selections: Immutable.List(
      selectionsUrlOverride(selections).map(p => new Product(p))
    ),
    currentColorId: currentColorUrlOverride(currentColorId),
    lastSavedSelections: null
  });
};

export const deserializeQuote = data => {
  const {postalCode, quantities, itemizedPrices, totalPrice, quoteBlank} =
    data.quote;
  return Immutable.Map({
    postalCode,
    itemizedPrices: Immutable.fromJS(itemizedPrices),
    totalPrice,
    quoteBlank,
    quantities: fromJSOrdered(quantitiesUrlOverride(quantities))
  });
};

export const deserialize = data => {
  return {
    canvas: deserializeCanvas(data),
    design: deserializeDesigns(data),
    icon: deserializeIcons(data)
  };
};
